import { logger } from './logger';

export interface PollingOptions {
  intervalMs: number;
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  maxConsecutiveErrors?: number;
  onError?: (error: Error, consecutiveErrors: number) => void;
  onCircuitOpen?: () => void;
  onCircuitClose?: () => void;
}

interface PollingState {
  isRunning: boolean;
  consecutiveErrors: number;
  isCircuitOpen: boolean;
  lastErrorTime: number | null;
  retryCount: number;
}

/**
 * Servicio de polling profesional con:
 * - Exponential backoff
 * - Circuit breaker
 * - Rate-limited error logging
 * - Retry automático
 */
export class PollingService {
  private state: PollingState = {
    isRunning: false,
    consecutiveErrors: 0,
    isCircuitOpen: false,
    lastErrorTime: null,
    retryCount: 0
  };

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private options: PollingOptions) {
    this.options = {
      maxRetries: 5,
      baseDelay: 1000,
      maxDelay: 30000,
      maxConsecutiveErrors: 5,
      ...options
    };
  }

  /**
   * Inicia el polling
   */
  start<T>(
    fetchData: () => Promise<T>,
    callback: (data: T) => void,
    onError?: (error: Error) => void
  ): () => void {
    if (this.state.isRunning) {
      logger.warn('[PollingService] Already running, stopping previous instance');
      this.stop();
    }

    this.state.isRunning = true;
    this.state.consecutiveErrors = 0;
    this.state.isCircuitOpen = false;
    this.state.retryCount = 0;

    const executeFetch = async () => {
      // Si el circuit breaker está abierto, no hacer nada
      if (this.state.isCircuitOpen) {
        logger.debug('[PollingService] Circuit breaker open, skipping fetch');
        return;
      }

      try {
        const data = await fetchData();
        
        // Resetear estado de errores en éxito
        if (this.state.consecutiveErrors > 0) {
          logger.info('[PollingService] Recovered from error state');
          this.state.consecutiveErrors = 0;
          this.state.retryCount = 0;
          this.options.onCircuitClose?.();
        }
        
        callback(data);
      } catch (error) {
        this.handleError(error as Error, onError);
      }
    };

    // Primera ejecución inmediata
    executeFetch();

    // Configurar intervalo
    this.intervalId = setInterval(executeFetch, this.options.intervalMs);

    // Retornar función de cleanup
    return () => this.stop();
  }

  /**
   * Maneja errores con exponential backoff y circuit breaker
   */
  private handleError(error: Error, onError?: (error: Error) => void): void {
    this.state.consecutiveErrors++;
    this.state.lastErrorTime = Date.now();

    // Notificar al callback de error
    onError?.(error);
    this.options.onError?.(error, this.state.consecutiveErrors);

    // Loggear error (con rate limiting implícito por el contador)
    if (this.state.consecutiveErrors <= this.options.maxConsecutiveErrors!) {
      logger.error(
        `[PollingService] Error ${this.state.consecutiveErrors}/${this.options.maxConsecutiveErrors}`,
        error
      );
    } else if (this.state.consecutiveErrors === this.options.maxConsecutiveErrors! + 1) {
      // Loggear solo una vez cuando se alcanza el límite
      logger.warn(
        `[PollingService] Reached max consecutive errors (${this.options.maxConsecutiveErrors}), ` +
        `suppressing further error logs until recovery`
      );
    }

    // Circuit breaker: abrir después de maxConsecutiveErrors errores
    if (this.state.consecutiveErrors >= this.options.maxConsecutiveErrors!) {
      this.openCircuit();
    }

    // Retry con exponential backoff
    if (this.state.retryCount < this.options.maxRetries!) {
      this.scheduleRetry();
    }
  }

  /**
   * Abre el circuit breaker
   */
  private openCircuit(): void {
    if (!this.state.isCircuitOpen) {
      this.state.isCircuitOpen = true;
      logger.error('[PollingService] Circuit breaker OPENED - stopping polling', new Error('Circuit breaker opened due to consecutive errors'));
      this.options.onCircuitOpen?.();
      
      // Programar reintento para cerrar el circuito
      this.scheduleCircuitRecovery();
    }
  }

  /**
   * Programa recuperación del circuit breaker
   */
  private scheduleCircuitRecovery(): void {
    const recoveryDelay = Math.min(
      this.options.maxDelay!,
      this.options.baseDelay! * Math.pow(2, this.options.maxRetries!)
    );

    logger.info(`[PollingService] Scheduling circuit recovery in ${recoveryDelay}ms`);

    this.timeoutId = setTimeout(() => {
      logger.info('[PollingService] Attempting circuit recovery...');
      this.state.isCircuitOpen = false;
      this.state.consecutiveErrors = 0;
      this.state.retryCount = 0;
    }, recoveryDelay);
  }

  /**
   * Programa retry con exponential backoff
   */
  private scheduleRetry(): void {
    const delay = Math.min(
      this.options.maxDelay!,
      this.options.baseDelay! * Math.pow(2, this.state.retryCount)
    );

    this.state.retryCount++;

    logger.debug(`[PollingService] Scheduling retry ${this.state.retryCount}/${this.options.maxRetries} in ${delay}ms`);

    this.timeoutId = setTimeout(() => {
      // El retry se ejecutará en el próximo intervalo normal
      logger.debug('[PollingService] Retry scheduled');
    }, delay);
  }

  /**
   * Detiene el polling
   */
  stop(): void {
    this.state.isRunning = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    logger.debug('[PollingService] Stopped');
  }

  /**
   * Retorna el estado actual
   */
  getState(): PollingState {
    return { ...this.state };
  }

  /**
   * Verifica si el polling está activo
   */
  isRunning(): boolean {
    return this.state.isRunning;
  }
}

/**
 * Utilidad para crear un polling service con opciones por defecto
 */
export const createPollingService = (
  intervalMs: number,
  options?: Partial<PollingOptions>
): PollingService => {
  return new PollingService({
    intervalMs,
    ...options
  });
};
